import React, { useEffect, useMemo, useState } from 'react';
import { contentApi } from '../../services/api/content.api';
import { GAME_MODES, LANGUAGES, type CriteriaCatalogItem, type GameMode, type Language } from '@business-game/shared';

export const AdminCriteriaPage: React.FC = () => {
  const [gameMode, setGameMode] = useState<GameMode>(GAME_MODES.TEAMLAB);
  const [language, setLanguage] = useState<Language>('fr');
  const [criterias, setCriterias] = useState<CriteriaCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [newCode, setNewCode] = useState('');
  const [newOrder, setNewOrder] = useState(0);
  const [savingId, setSavingId] = useState<number | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await contentApi.listCriteriaCatalog(gameMode);
      setCriterias(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [gameMode]);

  const rows = useMemo(
    () =>
      criterias.map((criteria) => {
        const translation = criteria.translations.find((entry) => entry.language === language);
        return {
          ...criteria,
          currentName: translation?.name || '',
          currentDescription: translation?.description || '',
        };
      }),
    [criterias, language]
  );

  const stats = useMemo(
    () => ({
      total: rows.length,
      active: rows.filter((criteria) => criteria.isActive).length,
      translatedNames: rows.filter((criteria) => criteria.currentName.trim().length > 0).length,
      translatedDescriptions: rows.filter((criteria) => criteria.currentDescription.trim().length > 0).length,
    }),
    [rows]
  );

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    await contentApi.createCriteriaCatalog({
      code: newCode,
      gameMode,
      order: newOrder,
      isActive: true,
    });
    setNewCode('');
    setNewOrder(criterias.length + 1);
    await loadData();
  };

  const handleRowSave = async (
    criteriaId: number,
    code: string,
    order: number,
    isActive: boolean,
    name: string,
    description: string
  ) => {
    setSavingId(criteriaId);
    try {
      await contentApi.updateCriteriaCatalog(criteriaId, {
        code,
        gameMode,
        order,
        isActive,
      });
      await contentApi.upsertCriteriaTranslation({
        criteriaId,
        language,
        name,
        description,
      });
      await loadData();
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="flex min-h-full flex-col font-sans text-slate-900">
      <main className="flex-grow w-full px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 w-full rounded-[2rem] border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-slate-50 p-6 shadow-sm">
          <div className="space-y-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500">Criteres</div>
            <h1 className="mt-2 text-2xl font-black tracking-tight text-slate-900">Catalogue des criteres</h1>
            <p className="mt-2 text-sm text-slate-500">
              Gere le catalogue canonique des criteres et leurs traductions globales par langue.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <StatPill label="Criteres" value={stats.total} />
            <StatPill label="Actifs" value={stats.active} accent="emerald" />
            <StatPill label={`Noms ${language.toUpperCase()}`} value={stats.translatedNames} />
            <StatPill label={`Descriptions ${language.toUpperCase()}`} value={stats.translatedDescriptions} accent="violet" />
          </div>

          <div className="grid gap-4 lg:grid-cols-[1fr_auto_auto]">
            <FilterCard label="Mode">
              <select
                value={gameMode}
                onChange={(event) => setGameMode(event.target.value as GameMode)}
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
              >
                <option value="teamlab">TeamLab</option>
                <option value="leadlab">LeadLab</option>
              </select>
            </FilterCard>

            <FilterCard label="Langue">
              <select
                value={language}
                onChange={(event) => setLanguage(event.target.value as Language)}
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
              >
                {LANGUAGES.map((entry) => (
                  <option key={entry} value={entry}>
                    {entry}
                  </option>
                ))}
              </select>
            </FilterCard>
          </div>

          <SurfaceCard className="p-5">
            <div className="mb-4">
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500">Ajout</div>
              <h2 className="mt-2 text-lg font-bold text-slate-900">Ajouter un critere</h2>
              <p className="mt-1 text-sm text-slate-500">
                Cree un nouveau critere dans le catalogue avec son code et son ordre d'affichage.
              </p>
            </div>

            <form onSubmit={handleCreate} className="grid gap-4 lg:grid-cols-[160px_140px_auto] lg:items-end">
              <label className="block">
                <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Code</span>
                <input
                  value={newCode}
                  onChange={(event) => setNewCode(event.target.value.toUpperCase().slice(0, 3))}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 font-mono focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ex: QVT"
                  required
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Ordre</span>
                <input
                  type="number"
                  value={newOrder}
                  onChange={(event) => setNewOrder(Number(event.target.value))}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ex: 1"
                />
              </label>

              <button className="rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition-all hover:-translate-y-0.5 hover:bg-indigo-700 active:scale-95">
                Ajouter un critere
              </button>
            </form>
          </SurfaceCard>

          <div className="space-y-4">
            <SurfaceCard className="hidden p-4 lg:block">
              <div className="grid grid-cols-[120px_90px_110px_minmax(180px,1fr)_minmax(260px,1.3fr)_120px] gap-4 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                <div>Code</div>
                <div>Ordre</div>
                <div>Actif</div>
                <div>Nom</div>
                <div>Description</div>
                <div>Action</div>
              </div>
            </SurfaceCard>

            {loading && <SurfaceCard className="p-6 text-sm text-slate-500">Chargement...</SurfaceCard>}

            {!loading &&
              rows.map((criteria) => (
                <CriteriaEditorCard
                  key={criteria.id}
                  criteria={criteria}
                  isSaving={savingId === criteria.id}
                  onSave={handleRowSave}
                />
              ))}
          </div>
          </div>
        </div>
      </main>
    </div>
  );
};

const CriteriaEditorCard: React.FC<{
  criteria: CriteriaCatalogItem & { currentName: string; currentDescription: string };
  isSaving: boolean;
  onSave: (
    criteriaId: number,
    code: string,
    order: number,
    isActive: boolean,
    name: string,
    description: string
  ) => Promise<void>;
}> = ({ criteria, isSaving, onSave }) => {
  const [code, setCode] = useState(criteria.code);
  const [order, setOrder] = useState(criteria.order);
  const [isActive, setIsActive] = useState(criteria.isActive);
  const [name, setName] = useState(criteria.currentName);
  const [description, setDescription] = useState(criteria.currentDescription);

  useEffect(() => {
    setCode(criteria.code);
    setOrder(criteria.order);
    setIsActive(criteria.isActive);
    setName(criteria.currentName);
    setDescription(criteria.currentDescription);
  }, [criteria]);

  return (
    <SurfaceCard className="p-4 hover:-translate-y-0.5">
      <div className="mb-3 flex items-center justify-between lg:hidden">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500">Critere</div>
          <div className="mt-1 text-lg font-bold text-slate-900">{criteria.code}</div>
        </div>
        <button
          type="button"
          onClick={() => void onSave(criteria.id, code, order, isActive, name, description)}
          className="rounded-2xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition-all hover:-translate-y-0.5 hover:bg-indigo-700 active:scale-95 disabled:opacity-50"
          disabled={isSaving}
        >
          {isSaving ? 'Sauvegarde...' : 'Sauver'}
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[120px_90px_110px_minmax(180px,1fr)_minmax(260px,1.3fr)_120px] lg:items-start">
        <label className="block">
          <span className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-slate-500 lg:sr-only">Code</span>
          <input
            value={code}
            onChange={(event) => setCode(event.target.value.toUpperCase().slice(0, 3))}
            className="w-full rounded-2xl border border-slate-200 px-4 py-3 font-mono focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
          />
        </label>

        <label className="block">
          <span className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-slate-500 lg:sr-only">Ordre</span>
          <input
            type="number"
            value={order}
            onChange={(event) => setOrder(Number(event.target.value))}
            className="w-full rounded-2xl border border-slate-200 px-4 py-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
          />
        </label>

        <label className="flex h-[50px] items-center gap-3 rounded-2xl border border-slate-200 px-4">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-sm font-medium text-slate-700">Actif</span>
        </label>

        <label className="block">
          <span className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-slate-500 lg:sr-only">Nom</span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="w-full rounded-2xl border border-slate-200 px-4 py-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
            placeholder="Nom traduit"
          />
        </label>

        <label className="block">
          <span className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-slate-500 lg:sr-only">Description</span>
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            className="min-h-[92px] w-full rounded-2xl border border-slate-200 px-4 py-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
            placeholder="Description traduite"
          />
        </label>

        <div className="hidden lg:flex lg:h-full lg:items-start lg:justify-end">
          <button
            type="button"
            onClick={() => void onSave(criteria.id, code, order, isActive, name, description)}
            className="w-full rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition-all hover:-translate-y-0.5 hover:bg-indigo-700 active:scale-95 disabled:opacity-50"
            disabled={isSaving}
          >
            {isSaving ? 'Sauvegarde...' : 'Sauver'}
          </button>
        </div>
      </div>
    </SurfaceCard>
  );
};

const FilterCard: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <SurfaceCard className="p-4">
    <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">{label}</label>
    {children}
  </SurfaceCard>
);

const SurfaceCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div
    className={`rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:border-indigo-200 hover:shadow-lg ${className}`.trim()}
  >
    {children}
  </div>
);

const StatPill: React.FC<{
  label: string;
  value: number;
  accent?: 'indigo' | 'violet' | 'emerald';
}> = ({ label, value, accent = 'indigo' }) => {
  const accentClass =
    accent === 'emerald'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : accent === 'violet'
        ? 'border-violet-200 bg-violet-50 text-violet-700'
        : 'border-indigo-200 bg-indigo-50 text-indigo-700';

  return (
    <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium ${accentClass}`}>
      <span className="text-slate-500">{label}</span>
      <span className="font-black text-slate-900">{value}</span>
    </div>
  );
};
